import { ADMIN_MODAL_FORMS, ADMIN_FLASH, ADMIN_LINEUP, ADMIN_FORMS } from '../../../constants/actions';
import { apiActionCreator, apiMultipleCalls } from '../../helpers';
import { stages, partners, events, artists, contracts, performances, roles } from '../../../services/api.admin';
import { BAD_REQUEST_CODE } from '../../../constants/http.codes';
import {
    stageFormToAPIData,
    stagesToState,
    stagesSelector,
    performanceToAPIData,
    isListViewSelector,
    selectedEntriesSelector
} from '../../../selectors/admin/models/lineup';
import { submit as formSubmit, fetchDependencies as fetchFormDependencies } from '../forms';
import { filter } from 'lodash';
import { openModalFormWithData } from '../modal.forms';
import { MODELS, BULK_PERFORMANCE_TYPES } from '../../../constants/admin/settings';
import { filterSigners } from '../../../selectors/admin/models/contracts';
import { contextSubjectSelector } from '../../../selectors/admin/context';
import { CONTRACT_TYPES } from '../../../constants/settings';

export function fetchLineup(eventId) {
    return (dispatch, getState) => {
        const context = contextSubjectSelector(getState());
        dispatch({ type: ADMIN_LINEUP.FETCH_START });
        return apiMultipleCalls(
            dispatch,
            [() => events.getTimetable(eventId), () => artists.getEventAll(eventId, context)],
            resp => {
                return dispatch({
                    type: ADMIN_LINEUP.FETCH_OK,
                    stages: stagesToState(resp[0].timetable.event.Stages),
                    artists: resp[1].artists
                });
            },
            0,
            {},
            true
        );
    };
}

export function createStage(eventId, data) {
    return dispatch => {
        dispatch({ type: ADMIN_MODAL_FORMS.SUBMIT });
        apiActionCreator(
            dispatch,
            () => stages.post(eventId, stageFormToAPIData(data)),
            resp => {
                dispatch({
                    type: ADMIN_LINEUP.ADD,
                    field: 'stages',
                    value: data.dates.map((v, i) => ({
                        number: data.number,
                        name: data[`name${v}`],
                        date: v,
                        id: resp.id
                    }))
                });
                dispatch({ type: ADMIN_MODAL_FORMS.SUCCESS });
                dispatch({
                    type: ADMIN_FLASH.OPEN,
                    message: `Stage successfully created.`
                });
            },
            {
                [BAD_REQUEST_CODE]: resp => {
                    dispatch({ error: resp.message, type: ADMIN_MODAL_FORMS.FAILED });
                }
            }
        );
    };
}

export function updateStage(eventId, data) {
    return dispatch => {
        dispatch({ type: ADMIN_MODAL_FORMS.SUBMIT });
        return apiActionCreator(
            dispatch,
            () => stages.put(eventId, data.id, stageFormToAPIData(data)),
            () => {
                dispatch({ type: ADMIN_MODAL_FORMS.SUCCESS });
                dispatch({ type: ADMIN_LINEUP.REFRESH });
            }
        );
    };
}

export function fetchEntryDependencies(eventId, contextId) {
    return fetchFormDependencies([() => partners.getAllForEvent(eventId, contextId), () => roles.getAll()], {
        append: true
    });
}

export function fetchContractSigningUsers(orgId, artistId, eventId, doneFunction) {
    return fetchFormDependencies([() => contracts.getSigners(artistId, orgId, CONTRACT_TYPES.CLEARANCE)], {
        append: true,
        parseFunction: res => ({
            signers: filterSigners(res.users, orgId, artistId, eventId, CONTRACT_TYPES.CLEARANCE)
        }),
        afterDone: doneFunction
    });
}

export function submitEntry(data, done) {
    return dispatch => {
        const isUpdate = !!data.id;
        const body = performanceToAPIData(data);
        return dispatch(
            formSubmit(
                isUpdate ? () => performances.put(data.id, body) : () => performances.post(body),
                `Performance successfully ${isUpdate ? 'updated' : 'created'}.`,
                {
                    cancelRedirect: true,
                    afterDone: () => {
                        dispatch({ type: ADMIN_LINEUP.REFRESH });
                        dispatch({ type: ADMIN_FORMS.SUCCESS });
                        done();
                    }
                }
            )
        );
    };
}

export function deleteEntry(id, done) {
    return dispatch => {
        return apiActionCreator(
            dispatch,
            () => performances.delete(id),
            () => {
                done();
                dispatch({ type: ADMIN_LINEUP.REFRESH });
                dispatch({ type: ADMIN_FLASH.OPEN, message: 'Entry successfully deleted.' });
            }
        );
    };
}

export function deleteStage(eventId, stageId, done) {
    return dispatch => {
        return apiActionCreator(
            dispatch,
            () => stages.delete(eventId, stageId),
            () => {
                done();
                dispatch({ type: ADMIN_LINEUP.REFRESH });
                dispatch({ type: ADMIN_FLASH.OPEN, message: 'Stage successfully deleted.' });
            }
        );
    };
}

export function lineupAdd(field, value) {
    return { type: ADMIN_LINEUP.ADD, field, value };
}

export function toggleListView() {
    return (dispatch, getState) => {
        dispatch({ type: ADMIN_LINEUP.UPDATE, field: 'listView', value: !isListViewSelector(getState()) });
        dispatch(clearPerformancesSelection());
    };
}

export function lineupUpdate(field, value) {
    return { type: ADMIN_LINEUP.UPDATE, field, value };
}

export function selectPerformance(id) {
    return (dispatch, getState) => {
        let selected = selectedEntriesSelector(getState());

        if (selected.includes(id)) {
            selected = filter(selected, s => s !== id);
        } else {
            selected = [...selected, id];
        }
        dispatch(lineupUpdate('selectedEntries', selected));
    };
}

export function clearPerformancesSelection() {
    return lineupUpdate('selectedEntries', []);
}

export function selectAllPerformances(ids) {
    return (dispatch, getState) => {
        const selected = selectedEntriesSelector(getState());
        if (selected.length) {
            dispatch(clearPerformancesSelection());
        } else {
            dispatch(lineupUpdate('selectedEntries', ids));
        }
    };
}

export function setAllLive() {
    return dispatch => {
        dispatch(
            openModalFormWithData(MODELS.BULK_PERFORMANCE, {
                type: BULK_PERFORMANCE_TYPES.LIVE,
                partners: [],
                [BULK_PERFORMANCE_TYPES.LIVE]: ''
            })
        );
    };
}
export function setAllNonLinear() {
    return dispatch => {
        dispatch(
            openModalFormWithData(MODELS.BULK_PERFORMANCE, {
                type: BULK_PERFORMANCE_TYPES.NON_LINEAR,
                partners: [],
                [BULK_PERFORMANCE_TYPES.NON_LINEAR]: ''
            })
        );
    };
}
export function setAllUntil() {
    return dispatch => {
        dispatch(
            openModalFormWithData(MODELS.BULK_PERFORMANCE, {
                type: BULK_PERFORMANCE_TYPES.USE_UNTIL,
                partners: [],
                [BULK_PERFORMANCE_TYPES.USE_UNTIL]: ''
            })
        );
    };
}
export function setAllComments() {
    return dispatch => {
        dispatch(
            openModalFormWithData(MODELS.BULK_PERFORMANCE, {
                type: BULK_PERFORMANCE_TYPES.COMMENTS,
                partners: [],
                [BULK_PERFORMANCE_TYPES.COMMENTS]: ''
            })
        );
    };
}
